import React, { useEffect, useState } from 'react'
import { Image, Platform } from 'react-native'
import { Svg, Rect, SvgProps } from 'react-native-svg'

import { useFloor, useWorkspaceTypeElements } from 'zens-store'

import FloorplanAsset from './asset'
import FloorplanLabel from './label'

interface ItemPosition {
  x: number
  y: number
  rotation: number
}

export type Item = Record<string, any> & {
  id: number
  floorplanElementId?: number
  floorplanPosition?: ItemPosition
  floorplanIsPlaced?: boolean
}

export interface Props extends SvgProps {
  companyId: number
  floorId: number
  width?: number
  height?: number
  data?: Item[]
  labelFontSize?: number
  itemColor?: (item: Item, index: number) => string | undefined
  itemBorderColor?: (item: Item, index: number) => string | undefined
  itemLabel?: (item: Item, index: number) => string | undefined
  onItemPress?: (item: Item, index: number) => void
  itemIsPressable?: (item: Item, index: number) => boolean
  onBackgroundPress?: () => void
  onContentSizeChange?: (contentSize: { width: number; height: number }) => void
  rotation?: 0 | 90 | 180 | 270
}

const FloorplanView = (props: Props) => {
  const {
    companyId,
    floorId,
    width,
    height,
    data: items,
    labelFontSize,
    itemColor,
    itemBorderColor,
    itemLabel,
    onItemPress,
    itemIsPressable,
    onBackgroundPress,
    onContentSizeChange,
    ...otherProps
  } = props
  const [contentRatio, setContentRatio] = useState(0)
  const floor = useFloor({ companyId, floorId })
  const elements = useWorkspaceTypeElements()

  const scale = floor.data?.floorplanScale
  function lengthBetweenPositions(positions: Omit<ItemPosition, 'rotation'>[]) {
    return Math.sqrt((positions[0]?.x - positions[1]?.x) ** 2 + (positions[0]?.y - positions[1]?.y) ** 2)
  }
  const scaleRatio = scale ? lengthBetweenPositions([scale.start, scale.end]) / scale.distance : 1
  const backgroundUri = floor.data?.floorplanBackground

  const viewBoxSize = { width: 4000, height: 4000 * contentRatio }
  const containerSize =
    width && height
      ? { width, height }
      : width
        ? { width, height: width * contentRatio }
        : height
          ? { width: height / contentRatio, height }
          : viewBoxSize

  useEffect(() => {
    Image.getSize(`${backgroundUri}`, (imageWidth, imageHeight) => {
      setContentRatio(imageHeight / imageWidth)
      onContentSizeChange?.({ width: imageWidth, height: imageHeight })
    })
  }, [backgroundUri, width, height])

  function renderWorkspace(item: Item, index: number) {
    if (!item.floorplanIsPlaced) return null
    const { id, floorplanElementId, floorplanPosition } = item
    const key = `${id}-${index}`
    const element = elements.data?.find((item) => item.id === floorplanElementId)
    const uri = element?.data?.src
    const width = Math.round((element?.data?.width || 0) * scaleRatio)
    const fill = itemColor?.(item, index)
    const stroke = itemBorderColor?.(item, index)
    const pressable = itemIsPressable?.(item, index) ?? true

    return (
      <FloorplanAsset
        key={key}
        uri={uri}
        width={width}
        placement={floorplanPosition}
        fill={fill}
        stroke={stroke}
        onPress={onItemPress && pressable ? () => onItemPress(item, index) : undefined}
      />
    )
  }
  function renderLabel(item: Item, index: number) {
    if (!item.floorplanIsPlaced) return null
    const label = itemLabel?.(item, index)
    if (!label) return null
    const { id, floorplanElementId, floorplanPosition } = item
    const key = `${id}-${index}`
    const element = elements.data?.find((item) => item.id === floorplanElementId)
    const uri = element?.data?.src
    const width = Math.round((element?.data?.width || 0) * scaleRatio)
    return (
      <FloorplanLabel
        key={key}
        label={label}
        uri={uri || ''}
        width={width}
        placement={floorplanPosition}
        fontSize={labelFontSize}
      />
    )
  }
  return (
    <Svg
      width={containerSize.width}
      height={containerSize.height}
      viewBox={`0 0 ${viewBoxSize.width} ${viewBoxSize.height}`}
      id="FloorplanView"
      // @ts-expect-error xmlns is required to download correct SVG files
      xmlns="http://www.w3.org/2000/svg"
      {...otherProps}
    >
      <Rect
        fill="white"
        width={viewBoxSize.width}
        height={viewBoxSize.height}
        {...Platform.select({
          web: { onClick: onBackgroundPress },
        })}
        onPress={onBackgroundPress}
        testID="Floorplan.Background"
      />
      <FloorplanAsset uri={backgroundUri} width={4000} />
      {items?.map(renderWorkspace)}
      {items?.map(renderLabel)}
    </Svg>
  )
}

export default FloorplanView
